// @ts-check
/**
 * resourceGroups.js - which kind of resource an engineer is, read off the two
 * fields every engineer already has: Region and Employment (10/16/26).
 *
 * Sharif: the engineer CARDS show only internal engineers and interns; the
 * Resources view shows everyone - internal, interns, contractors, MENA and
 * South Africa - split in half: internal / interns / MENA on the left, South
 * Africa top right, contractors bottom right.
 *
 * In this order, first match wins (Sharif, 10/16/26):
 *   contractor  Employment = Contractor, or the legacy Region "1099"
 *   sa          Region SA (South Africa)
 *   mena        Region MEA (Middle East / GLOBAL KSA)
 *   eu          Region EU (Europe) - not internal: no card, never utilization
 *   intern      Employment = Intern
 *   internal    everyone else: NA or no region, employee
 * So a South African contractor is a contractor, and an intern in South
 * Africa is listed with South Africa. Both fields are on the engineer form.
 * Only `internal` counts toward utilization (utils/utilizationFromCalendar).
 * Server twin: server/lib/calendarTables.js resourceGroupOf - keep in step.
 */

export const RESOURCE_GROUPS = [
  { key: 'internal', label: 'Internal engineers', side: 'left' },
  { key: 'intern', label: 'Interns', side: 'left' },
  { key: 'mena', label: 'MENA', side: 'left' },
  // shown only when someone is in it (ResourcesTable)
  { key: 'eu', label: 'Europe', side: 'left', hideEmpty: true },
  { key: 'sa', label: 'South Africa', side: 'right' },
  { key: 'contractor', label: 'Contractors', side: 'right' },
];

/** The groups whose people get an engineer card. */
export const CARD_GROUPS = ['internal', 'intern'];

const norm = (v) =>
  String(v ?? '')
    .trim()
    .toLowerCase();
const SA = new Set(['sa', 'south africa', 'za', 'rsa']);
const MENA = new Set(['mea', 'mena', 'middle east', 'ksa', 'global ksa']);
const EU = new Set(['eu', 'europe']);

/**
 * 'internal' | 'intern' | 'mena' | 'eu' | 'sa' | 'contractor'
 * @param {Record<string, any> | null | undefined} e an engineers row
 * @returns {string}
 */
export function resourceGroupOf(e) {
  const employment = norm(e && (e.employmentType ?? e.employment_type));
  const region = norm(e && (e.region ?? e.Region));
  if (employment === 'contractor' || region === '1099') return 'contractor';
  if (SA.has(region)) return 'sa';
  if (MENA.has(region)) return 'mena';
  if (EU.has(region)) return 'eu';
  if (employment === 'intern') return 'intern';
  return 'internal';
}

/** True when this engineer gets a card on the Engineers tab. @param {Record<string, any> | null | undefined} e */
export const showsOnCards = (e) => CARD_GROUPS.includes(resourceGroupOf(e));

export const resourceGroupLabel = (key) => (RESOURCE_GROUPS.find((g) => g.key === key) || {}).label || '';
